import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/css';
import Button from '../../../../shared/components/general/Button';
import IconTooltip from '../../../../shared/components/general/IconTooltip';
import SelectField from '../../../../shared/components/forms/SelectField';
import { color_colors_ocean, color_shades_dark, color_shades_lighter, dark_grey_blue } from '../../../../shared/constants/colors';
import { getOutboundCloneSources, OutboundCloneSource } from '../../services/outboundAutomation';

const infoIcon = require('../../../../shared/content/images/info_icon.svg');
const customTooltip = 'Create a custom outbound configuration for this location when its settings differ from other locations in the organization.';
const cloneTooltip = 'This will copy the outbound configuration from another location in this organization, so you don’t have to enter the same information for each location.';

interface OutboundSetupChoiceProps {
    applicationId: string;
    orgId?: string;
    mode: 'manual' | 'clone';
    selectedSourceId?: string;
    manualScheduleSaved?: boolean;
    onModeChange: (mode: 'manual' | 'clone') => void;
    onSourceChange: (source?: OutboundCloneSource) => void;
    onOpenSchedule: () => void;
}

const OutboundSetupChoice: React.FC<OutboundSetupChoiceProps> = (props) => {
    const [sources, setSources] = useState<OutboundCloneSource[]>([]);

    useEffect(() => {
        let mounted = true;
        if (!props.orgId) return () => { mounted = false; };

        getOutboundCloneSources(props.orgId, props.applicationId)
            .then(result => {
                if (!mounted) return;
                setSources(result);
                if (result.length === 0 && props.mode === 'clone') {
                    props.onModeChange('manual');
                    props.onSourceChange(undefined);
                }
            })
            .catch(() => {
                if (!mounted) return;
                setSources([]);
                if (props.mode === 'clone') {
                    props.onModeChange('manual');
                    props.onSourceChange(undefined);
                }
            });

        return () => { mounted = false; };
    }, [props.orgId, props.applicationId]);

    const selectedSource = sources.find(source => source.applicationId === props.selectedSourceId);
    const hasCloneSources = sources.length > 0;
    const chooseMode = (mode: 'manual' | 'clone') => {
        props.onModeChange(mode);
        if (mode === 'manual') {
            props.onSourceChange(undefined);
        } else if (sources.length === 1) {
            props.onSourceChange(sources[0]);
        }
    };

    if (!hasCloneSources) {
        return (
            <ActionArea>
                <ScheduleButton
                    themes={[props.manualScheduleSaved ? 'secondary-small' : 'primary-small']}
                    text={props.manualScheduleSaved ? 'Edit the Schedule' : 'Set the Schedule'}
                    onClick={props.onOpenSchedule}
                    type="button"
                />
            </ActionArea>
        );
    }

    return (
        <ChoiceArea>
            <ChoiceLabel>How would you like to configure outbound?</ChoiceLabel>
            <Choices role="radiogroup" aria-label="Outbound configuration method">
                <ChoiceButton
                    type="button"
                    role="radio"
                    aria-checked={props.mode === 'manual'}
                    selected={props.mode === 'manual'}
                    onClick={() => chooseMode('manual')}
                >
                    <RadioMark selected={props.mode === 'manual'} />
                    <span>Custom</span>
                    <IconTooltip text={customTooltip} icon={infoIcon} place="top" maxWidth={320} />
                </ChoiceButton>
                <ChoiceButton
                    type="button"
                    role="radio"
                    aria-checked={props.mode === 'clone'}
                    selected={props.mode === 'clone'}
                    onClick={() => chooseMode('clone')}
                >
                    <RadioMark selected={props.mode === 'clone'} />
                    <span>Clone</span>
                    <IconTooltip text={cloneTooltip} icon={infoIcon} place="top" maxWidth={320} />
                </ChoiceButton>
            </Choices>

            {props.mode === 'manual' ? (
                <ActionArea>
                    <ScheduleButton
                        themes={[props.manualScheduleSaved ? 'secondary-small' : 'primary-small']}
                        text={props.manualScheduleSaved ? 'Edit the Schedule' : 'Set the Schedule'}
                        onClick={props.onOpenSchedule}
                        type="button"
                    />
                </ActionArea>
            ) : (
                <CloneFields>
                    <SelectField
                        name="outboundCloneSource"
                        label="Outbound Configuration for Location Name"
                        labelStyle={outboundLabelStyle}
                        options={sources.map(source => ({ value: source.applicationId, label: source.appName }))}
                        value={props.selectedSourceId || ''}
                        onChange={(option) => props.onSourceChange(sources.find(source => source.applicationId === option?.value))}
                        placeholder="Select a location"
                        clearable={false}
                        useForm={false}
                        useFormTheme={true}
                    />
                    <ScheduleButton
                        disabled={!selectedSource}
                        themes={['primary-small']}
                        text="Confirm the Schedule"
                        onClick={props.onOpenSchedule}
                        type="button"
                    />
                </CloneFields>
            )}
        </ChoiceArea>
    );
};

const ChoiceArea = styled.div`
    margin-top: 12px;
    max-width: 620px;
`;

const ChoiceLabel = styled.p`
    color: ${dark_grey_blue};
    font-family: Muli;
    font-size: 14px;
    font-weight: normal;
    line-height: normal;
    margin: 0 0 8px;
`;

const outboundLabelStyle = css`
    color: ${dark_grey_blue};
    font-family: Muli;
    font-size: 14px;
    font-weight: normal;
    line-height: normal;
    margin: 0;
`;

const Choices = styled.div`
    display: flex;
    gap: 12px;
    margin-bottom: 16px;
`;

const ChoiceButton = styled.button<{ selected: boolean }>`
    align-items: center;
    background: ${props => props.selected ? color_shades_lighter : '#fff'};
    border: 1px solid ${props => props.selected ? color_colors_ocean : color_shades_dark};
    border-radius: 6px;
    color: ${dark_grey_blue};
    cursor: pointer;
    display: flex;
    font-family: Muli;
    font-size: 14px;
    gap: 8px;
    min-height: 44px;
    padding: 8px 14px;

    &:focus {
        outline: 2px solid ${color_colors_ocean};
        outline-offset: 2px;
    }
`;

const RadioMark = styled.span<{ selected: boolean }>`
    align-items: center;
    background: #fff;
    border: 1px solid ${props => props.selected ? color_colors_ocean : color_shades_dark};
    border-radius: 50%;
    display: flex;
    height: 18px;
    justify-content: center;
    width: 18px;

    &::after {
        background: ${color_colors_ocean};
        border-radius: 50%;
        content: '';
        display: ${props => props.selected ? 'block' : 'none'};
        height: 10px;
        width: 10px;
    }
`;

const ActionArea = styled.div`
    margin-top: 12px;
`;

const ScheduleButton = styled(Button)`
    margin-left: 0;
`;

const CloneFields = styled.div`
    .selector {
        margin-bottom: 12px;
    }

    .selector > div:first-child {
        margin-bottom: 8px;
    }
`;

export default OutboundSetupChoice;
